/**
 * Drafts (FEAT-017, re-homed by FEAT-038 wave 2).
 *
 * The instant the shutter fires, the capture is written to disk. A moment
 * leaves only when it is delivered (the queue says so) or the user explicitly
 * discards it. Reload, update, crash, iOS killing the PWA — none of them can
 * destroy a captured moment.
 *
 * What wave 2 changed: a draft is no longer a record in its own database with
 * a video inside it. It is a `media-meta` row with `draft: true` — the same row
 * the send queue points at. One capture, one record, one owner. The two-store
 * arrangement is what let a capture be counted, swept and deleted by two
 * services at once, and it is what made every count of the drafts sheet a read
 * of every video on the phone.
 *
 * The rule that survives from the old file, because it cost three unopenable
 * weeks: EVERYTHING here reads metadata. `mediaFor` is the one call that
 * returns bytes, it takes ONE id, and nothing may call it in a loop — a
 * single-row read repeated nine times is the same gigabyte by another spelling
 * (BUG-120).
 */
import type { CapturedMedia, DrawingStroke, MomentOverlays } from '../../types';
import { useToastStore } from '../../store/toastStore';
import { mediaStore, type MediaMeta } from './mediaStore';
import { evacuation } from './evacuate';
import { ledger } from '../queue/ledger';
import { sendEngine } from '../queue/engine';

/** A capture nobody sent and nobody came back for stops waiting after a week
 * (BUG-075). Long enough that "I'll finish it tomorrow" always works; short
 * enough that the drafts badge returns to zero on its own. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Nothing disappears as a surprise: the sheet warns inside this window. */
export const DRAFT_EXPIRY_WARN_MS = 2 * 24 * 60 * 60 * 1000;
/** How long after a draft was stamped "sending" we start treating it as a
 * possible phantom worth asking the server about. */
export const SEND_SETTLE_MS = 15_000;

/**
 * The phone width a pre-wave-2 stroke's thickness was measured against.
 *
 * `brushSize` used to be raw screen pixels (BUG-112). Nothing recorded WHICH
 * screen, so there is no exact conversion — but every device this app has run
 * on is a phone in portrait, and 390 CSS px is what those are.
 */
const LEGACY_BRUSH_BASIS = 390;

type LegacyStroke = DrawingStroke & { brushSize?: number };

export function migrateStroke(stroke: LegacyStroke): DrawingStroke {
  if (typeof stroke.w === 'number') return stroke;
  const { brushSize, ...rest } = stroke;
  return { ...rest, w: (brushSize ?? 8) / LEGACY_BRUSH_BASIS };
}

/** One draft, WITHOUT its media — which is every read but `mediaFor`. */
export type DraftMeta = MediaMeta;

/** A draft group over any row shape that carries the grouping fields. */
export interface Grouped<T> {
  key: string;
  batchId?: string;
  shots: T[];
  inEdit: T | null;
  createdAt: number;
  updatedAt: number;
}

/** One tile in the drafts sheet: a lone capture, or a batch plus its in-edit
 * shot. */
export type DraftGroup = Grouped<DraftMeta>;

interface PendingEdit {
  textBars?: { text: string; y: number }[];
  drawingStrokes?: DrawingStroke[];
  hd?: boolean;
  /** A poster extracted after the fact (a picked clip's first frame). Rides
   * the edit path because that path already waits for the row's own save. */
  thumbnail?: MediaMeta['thumbnail'];
}

type GroupableRow = {
  id: string;
  kind: 'single' | 'batch-shot';
  batchId?: string;
  batchIndex?: number;
  createdAt: number;
  updatedAt: number;
};

/**
 * The grouping. Counting a person's drafts and listing them are the same
 * question asked of different amounts of data, and neither has any business
 * reading a video off the disk.
 */
export function groupDraftRows<T extends GroupableRow>(rows: T[]): Grouped<T>[] {
  // INVARIANT (BUG-075): every row on disk lands in exactly one group. It used
  // to key purely on batchId and overwrite `inEdit` per row, so once
  // cameraStore leaked a batch id, N independent moments collapsed into ONE
  // tile — all but the last invisible AND undeletable. A capture belongs to a
  // batch tile only while that batch still has queued shots.
  const liveBatches = new Set(
    rows
      .filter((r) => r.kind === 'batch-shot' && r.batchId)
      .map((r) => r.batchId as string)
  );
  const groups = new Map<string, Grouped<T>>();
  const groupFor = (key: string, batchId?: string): Grouped<T> => {
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        batchId,
        shots: [],
        inEdit: null,
        createdAt: Number.POSITIVE_INFINITY, // Math.min'd down by its rows
        updatedAt: 0,
      };
      groups.set(key, group);
    }
    return group;
  };
  for (const row of rows) {
    const batched = !!row.batchId && liveBatches.has(row.batchId);
    let group: Grouped<T>;
    if (row.kind === 'batch-shot') {
      group = groupFor(batched ? (row.batchId as string) : row.id, row.batchId);
      group.shots.push(row);
    } else {
      // One editor, one in-edit shot. A second claimant on the same batch is
      // its own capture — never an overwrite that loses a row.
      const batchGroup = batched ? groupFor(row.batchId as string, row.batchId) : null;
      group = batchGroup && !batchGroup.inEdit ? batchGroup : groupFor(row.id);
      group.inEdit = row;
    }
    group.createdAt = Math.min(group.createdAt, row.createdAt);
    group.updatedAt = Math.max(group.updatedAt, row.updatedAt);
  }
  for (const group of groups.values()) {
    group.shots.sort((a, b) => (a.batchIndex ?? 0) - (b.batchIndex ?? 0));
  }
  return [...groups.values()].sort((a, b) => b.updatedAt - a.updatedAt);
}

export class DraftsService {
  /** Row ids that predate this launch — the "orphan" set behind the pill. */
  private bootIds = new Set<string>();
  private booted = false;
  private pendingEdits = new Map<string, PendingEdit>();
  private editTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private changeCallbacks = new Set<() => void>();
  /** In-flight saveCapture writes. Every mutation of an id awaits its entry
   * first — saveCapture is fired un-awaited from the shutter, so a fast edit,
   * promotion or delete could otherwise run before the row exists. */
  private pendingSaves = new Map<string, Promise<boolean>>();
  private unsubscribeStore: (() => void) | null = null;

  constructor(private editDebounceMs = 500) {}

  /** Snapshot what survived from previous sessions — these are the orphans the
   * recovery pill announces. Metadata only. */
  async init(): Promise<void> {
    if (this.booted) return;
    this.booted = true;
    if (!this.unsubscribeStore) {
      // The media store owns the rows now, so its change signal is ours.
      this.unsubscribeStore = mediaStore.onChange(() => this.fireChange());
    }
    if (typeof document !== 'undefined') {
      // Edits must not die with the tab: flush on every "going away" signal
      // (beforeunload is a lie on iOS PWAs — these are not).
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void this.flushEdits();
      });
      window.addEventListener('pagehide', () => void this.flushEdits());
    }
    try {
      // Wave 1's evacuation is still moving bytes out of the retired records
      // at this point on an upgrading phone; counting before it finishes would
      // report a number that is about to change. Memoized, and it never
      // rejects — a broken evacuation costs a stale count, never a boot.
      await evacuation.done();
      const ids = await mediaStore.allMetaIds();
      this.bootIds = new Set(ids);
      if (this.bootIds.size > 0) this.fireChange();
    } catch (err) {
      console.error('[Drafts] boot snapshot failed:', err);
    }
  }

  private async rowsFor(userId: number): Promise<DraftMeta[]> {
    await this.init();
    const rows = await mediaStore.listMeta(userId);
    return rows
      .filter((row) => row.draft)
      .map((row) => ({
        ...row,
        // The editor resumes from these (BUG-112).
        drawingStrokes: (row.drawingStrokes ?? []).map(migrateStroke),
      }));
  }

  /**
   * Persist a fresh capture. Called un-awaited from the shutter paths —
   * refusal is honest (toast + false) and never throws into the capture flow.
   */
  saveCapture(media: CapturedMedia, userId: number, batchId?: string): Promise<boolean> {
    const id = media.id;
    const work = this.doSaveCapture(id, media, userId, batchId);
    this.pendingSaves.set(id, work);
    void work.finally(() => {
      if (this.pendingSaves.get(id) === work) this.pendingSaves.delete(id);
    });
    return work;
  }

  private async doSaveCapture(
    id: string,
    media: CapturedMedia,
    userId: number,
    batchId?: string
  ): Promise<boolean> {
    try {
      await this.init();
      const now = Date.now();
      // The budget lives in the media store now — one place that knows what
      // this phone is holding, instead of two that each knew half.
      const saved = await mediaStore.saveBlob(
        {
          id,
          userId,
          mediaType: media.type,
          mimeType: media.blob.type,
          width: media.width,
          height: media.height,
          duration: media.duration,
          facing: media.facing,
          hd: media.hd,
          kind: 'single',
          // Taken while the frame was still on screen, so the drafts sheet and
          // the send queue can look like the moment they stand for without
          // anything going near the chunk store (FEAT-038 wave 3, §D).
          thumbnail: media.thumbnail,
          batchId,
          textBars: [],
          drawingStrokes: [],
          overlays: null,
          draft: true,
          createdAt: now,
        },
        media.blob
      );
      this.fireChange();
      // The bytes are safely on disk, so they can start moving NOW rather than
      // at send-tap — the caption and the friend-picking is where the seconds
      // go. Big clips only, and it writes no queue row: see engine.preUpload.
      void sendEngine.preUpload(saved.id, saved.size);
      return true;
    } catch (err) {
      console.error('[Drafts] saveCapture failed:', err);
      this.refuseToast();
      return false;
    }
  }

  /** Keep the draft current while the user edits. Debounced per capture. */
  updateEdits(id: string | undefined, edits: PendingEdit): void {
    if (!id) return;
    this.pendingEdits.set(id, { ...this.pendingEdits.get(id), ...edits });
    const existing = this.editTimers.get(id);
    if (existing) clearTimeout(existing);
    this.editTimers.set(
      id,
      setTimeout(() => void this.flushEditsFor(id), this.editDebounceMs)
    );
  }

  /** Write every pending edit NOW (page hiding, or a read/remove needs truth). */
  async flushEdits(): Promise<void> {
    const ids = [...this.pendingEdits.keys()];
    await Promise.all(ids.map((id) => this.flushEditsFor(id)));
  }

  private async flushEditsFor(id: string): Promise<void> {
    const timer = this.editTimers.get(id);
    if (timer) clearTimeout(timer);
    this.editTimers.delete(id);
    const edits = this.pendingEdits.get(id);
    if (!edits) return;
    this.pendingEdits.delete(id);
    try {
      await this.pendingSaves.get(id); // never run ahead of the row's own save
      await mediaStore.updateMeta(id, (row) => ({ ...row, ...edits }));
    } catch (err) {
      console.error('[Drafts] edit flush failed:', err);
    }
  }

  /** The raw in-edit row becomes its baked, queued batch shot. */
  async promoteToBatchShot(
    id: string | undefined,
    blob: Blob,
    batchId: string,
    batchIndex: number,
    overlays?: MomentOverlays | null
  ): Promise<void> {
    if (!id) return;
    try {
      await this.flushEditsFor(id);
      await this.pendingSaves.get(id);
      const row = await mediaStore.meta(id);
      if (!row) return;
      // FEAT-028 wave 2: the shot keeps the camera's own bytes and carries its
      // decoration as data. Losing the overlays here would lose the words a
      // crash-resumed draft was supposed to keep.
      const promotion = {
        kind: 'batch-shot' as const,
        batchId,
        batchIndex,
        overlays: overlays ?? null,
        textBars: [],
        drawingStrokes: [],
      };
      if (row.size === blob.size) {
        // Since wave 2 the baked blob IS the stored one. Same size means same
        // bytes, so a promotion is a change of labels and nothing may go near
        // the media — the common path, and the one that must stay cheap.
        await mediaStore.updateMeta(id, (current) => ({ ...current, ...promotion }));
      } else {
        // Different bytes under the same id, so any head start on the old ones
        // is void — resuming it would append new bytes onto an old offset and
        // only find out at the finalize checksum, having paid for the upload.
        await sendEngine.abandonUpload([id]);
        await mediaStore.remove([id], 'discarded');
        await mediaStore.saveBlob(
          {
            ...row,
            ...promotion,
            id,
            mimeType: blob.type || row.mimeType,
            draft: true,
          },
          blob,
          { bypassBudget: true }
        );
      }
      this.fireChange();
    } catch (err) {
      console.error('[Drafts] batch promotion failed:', err);
    }
  }

  /** A queued batch shot pulled back into the editor is in-edit again —
   * without this its later edits would be invisible to restore (grouping
   * treats 'batch-shot' rows as sealed). */
  async demoteToSingle(id: string | undefined): Promise<void> {
    if (!id) return;
    try {
      await this.pendingSaves.get(id);
      await mediaStore.updateMeta(id, (row) => ({
        ...row,
        kind: 'single',
        batchIndex: undefined,
      }));
      this.fireChange();
    } catch (err) {
      console.error('[Drafts] demote failed:', err);
    }
  }

  /**
   * Stamp the exact idempotency keys this draft is being sent under, right
   * before it goes on the wire (BUG-065).
   *
   * A draft is deleted only when the CLIENT processes the server's confirm.
   * Die in that window and a delivered moment lives on as an "unsent" draft
   * forever. The stamp is what lets the next launch ask "did these land?".
   */
  async markSending(id: string | undefined, keys: string[]): Promise<void> {
    if (!id || keys.length === 0) return;
    try {
      await this.pendingSaves.get(id);
      await mediaStore.updateMeta(id, (row) => ({
        ...row,
        sentKeys: [...new Set([...(row.sentKeys ?? []), ...keys])],
        sentAt: Date.now(),
      }));
    } catch (err) {
      console.error('[Drafts] markSending failed:', err);
    }
  }

  /**
   * Take keys back off a draft that can never land (FEAT-024).
   *
   * sendReconcile clears a draft only when EVERY stamped key landed, so a
   * single permanently-refused key among landed ones is exactly what makes a
   * DELIVERED moment sit in the drafts sheet forever (BUG-075). When the last
   * key goes, the whole stamp goes: the row is a plain unsent capture again,
   * visible, resumable and sweepable at seven days.
   */
  async unstampKeys(id: string | undefined, keys: string[]): Promise<void> {
    if (!id || keys.length === 0) return;
    const dead = new Set(keys);
    try {
      await this.pendingSaves.get(id);
      await mediaStore.updateMeta(id, (row) => {
        const left = (row.sentKeys ?? []).filter((key) => !dead.has(key));
        if (left.length > 0) return { ...row, sentKeys: left };
        return { ...row, sentKeys: undefined, sentAt: undefined };
      });
    } catch (err) {
      console.error('[Drafts] unstampKeys failed:', err);
    }
  }

  /**
   * Drafts worth asking the server about, with whatever they can be asked by:
   * their stamped keys, or (rows written before stamping existed) nothing, in
   * which case the capture id itself is the question.
   */
  async stampedCandidates(
    userId: number,
    settleMs = SEND_SETTLE_MS
  ): Promise<{ id: string; keys: string[] }[]> {
    const now = Date.now();
    const rows = await this.rowsFor(userId);
    return rows
      .filter(
        (r) =>
          // survived a launch: whatever send touched it is definitively over
          this.bootIds.has(r.id) ||
          // written this session: only once its send has had time to settle
          (r.sentAt !== undefined && now - r.sentAt >= settleMs)
      )
      .map((r) => ({ id: r.id, keys: r.sentKeys ?? [] }));
  }

  /**
   * Captures nobody sent and nobody came back for stop waiting after a week.
   * A row that was ever dispatched (it carries sentKeys) is NEVER swept on a
   * timer — only the server's answer may remove those, via sendReconcile.
   */
  async sweepExpired(userId: number, maxAgeMs = DRAFT_MAX_AGE_MS): Promise<number> {
    const cutoff = Date.now() - maxAgeMs;
    const rows = await this.rowsFor(userId);
    const stale = rows
      .filter((r) => !r.sentKeys?.length && r.createdAt <= cutoff)
      .map((r) => r.id);
    if (stale.length > 0) await this.remove(stale, 'expired');
    return stale.length;
  }

  /**
   * ONE capture's bytes, on the user's tap. Never in a loop — see the header.
   * Null when the row is gone or its chunks do not check out, which is an
   * honest failure rather than half a video going out as a real send.
   */
  async mediaFor(
    id: string
  ): Promise<{ blob: Blob; mediaType: 'photo' | 'video'; hd?: boolean } | null> {
    try {
      await this.flushEdits();
      const row = await mediaStore.meta(id);
      if (!row) return null;
      const blob = await mediaStore.assembleBlob(id);
      if (!blob) {
        console.error('[Drafts] media unreadable for draft', id);
        return null;
      }
      return { blob, mediaType: row.mediaType, hd: row.hd };
    } catch {
      return null;
    }
  }

  /** Delivered, discarded or aged out — either way the moment no longer needs
   * us, and its bytes go with it. */
  async remove(
    ids: (string | undefined)[],
    reason: 'delivered' | 'discarded' | 'expired'
  ): Promise<void> {
    const real = ids.filter((id): id is string => !!id);
    if (real.length === 0) return;
    for (const id of real) {
      const timer = this.editTimers.get(id);
      if (timer) clearTimeout(timer);
      this.editTimers.delete(id);
      this.pendingEdits.delete(id);
      this.bootIds.delete(id);
    }
    try {
      // A delete must not lose to a still-landing save (which would resurrect
      // the row moments after it was removed).
      await Promise.all(real.map((id) => this.pendingSaves.get(id)));
      // The ledger has to be loaded before it can be asked who owns what: an
      // empty mirror would answer "nobody", and a Memories save would delete
      // the upload it had just queued.
      await ledger.init();
      // A moment the queue is still sending keeps its BYTES and loses only its
      // draft-ness. Deleting them here is how a Memories save used to destroy
      // the very upload it had just queued: one record, two owners, and the
      // first one to finish took the media with it.
      const claimed = new Set(
        real.filter((id) => ledger.byMedia(id).length > 0)
      );
      for (const id of claimed) {
        await mediaStore.updateMeta(id, (row) => ({ ...row, draft: false }));
      }
      const gone = real.filter((id) => !claimed.has(id));
      if (gone.length > 0) {
        // A head start dies with the draft that owned it (FEAT-038 wave 3):
        // the append is stopped first, then the server is told to forget the
        // partial. Not on 'delivered' — that upload was consumed by the send
        // that has just landed, not abandoned.
        if (reason !== 'delivered') await sendEngine.abandonUpload(gone);
        await mediaStore.remove(gone, reason);
      }
      console.log(
        `[Drafts] removed ${real.length} (${reason})` +
          (claimed.size > 0 ? `, ${claimed.size} still queued` : '')
      );
      this.fireChange();
    } catch (err) {
      // The rows stay, which is the safe direction — but a row kept after a
      // DELIVERED send now lies about a moment that did arrive. Loud on
      // purpose (BUG-075): the next reconcile pass clears it within seconds.
      console.error('[Drafts] remove FAILED — rows kept, awaiting reconcile:', err);
    }
  }

  /** Groups for the drafts sheet, newest first; batch shots in order.
   * `exclude` hides media the queue is actively delivering — a just-sent
   * moment must not look unsent. */
  async listForUser(userId: number, exclude?: Set<string>): Promise<DraftGroup[]> {
    await this.flushEdits();
    return this.groupsFor(userId, exclude);
  }

  private async groupsFor(
    userId: number,
    exclude?: Set<string>
  ): Promise<DraftGroup[]> {
    const rows = (await this.rowsFor(userId)).filter((r) => !exclude?.has(r.id));
    return groupDraftRows(rows);
  }

  /** Everything the user has on disk (drives the camera drafts button).
   * Metadata only: this runs at BOOT, and reading nine videos to learn the
   * number nine is what stopped the app opening. */
  async countForUser(userId: number, exclude?: Set<string>): Promise<number> {
    return (await this.groupsFor(userId, exclude)).length;
  }

  /** Groups that predate this launch (drives the recovery pill). Boot path. */
  async orphanCountForUser(userId: number, exclude?: Set<string>): Promise<number> {
    const groups = await this.groupsFor(userId, exclude);
    return groups.filter((g) =>
      [...g.shots, ...(g.inEdit ? [g.inEdit] : [])].every((r) => this.bootIds.has(r.id))
    ).length;
  }

  /** A resumed draft is no longer an orphan — it's live work again. */
  markResumed(ids: (string | undefined)[]): void {
    for (const id of ids) if (id) this.bootIds.delete(id);
    this.fireChange();
  }

  /**
   * Adopt media the wave-2 migration could not attribute to anyone.
   *
   * A row the retired outbox was holding with no destination left (BUG-119's
   * victims) becomes a draft — the moment is safe, and re-sending it is the
   * user's call, because guessing a recipient is the one thing this campaign
   * must never do. Outbox rows carried no owner, so the first person to sign
   * in on this phone claims them; Momento is one account per device.
   */
  async adoptOwnerless(userId: number): Promise<number> {
    const rows = await mediaStore.listMeta(0);
    const orphans = rows.filter((row) => row.draft);
    for (const row of orphans) {
      await mediaStore.updateMeta(row.id, (current) => ({ ...current, userId }));
    }
    if (orphans.length > 0) {
      console.info(`[Drafts] adopted ${orphans.length} moment(s) with no recipient`);
      this.fireChange();
    }
    return orphans.length;
  }

  onChange(callback: () => void): () => void {
    this.changeCallbacks.add(callback);
    callback();
    return () => this.changeCallbacks.delete(callback);
  }

  /** Tests only. */
  close(): void {
    for (const timer of this.editTimers.values()) clearTimeout(timer);
    this.editTimers.clear();
    this.pendingEdits.clear();
    this.changeCallbacks.clear();
    this.bootIds.clear();
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;
    this.booted = false;
  }

  private fireChange(): void {
    for (const cb of this.changeCallbacks) cb();
  }

  private refuseToast(): void {
    useToastStore
      .getState()
      .show("Couldn't protect that moment — draft storage is full", 'error');
  }
}

export const drafts = new DraftsService();
